// React
import { useState, useRef, useEffect, useCallback } from 'react';

// UI primitives
import { Button } from '@/components/ui/button';

// Icons
import { ChevronIcon } from '@/react/components/icons/ChevronIcon';

// Hooks and utilities
import { cn } from '@/lib/utils';

const TAB_SCROLL_DISTANCE = 120;
const SCROLL_THRESHOLD_PX = 1;
const ARROW_BUTTON_SIZE_PX = 30;

function ScrollArrow({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const isLeft = direction === 'left';
  return (
    <div
      className={cn(
        'absolute inset-y-0 flex items-center pointer-events-none',
        isLeft ? 'left-0' : 'right-0',
      )}
    >
      {/* Left variant: arrow button sits BEFORE the fade (so the fade blends into the scroll area) */}
      {isLeft && (
        <div className="flex h-full items-center pl-1.5 bg-off-white pointer-events-auto">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClick}
            aria-label="Scroll left"
            // tw-safelist: size-[30px]
            className={cn('rounded-full bg-dinnerplate-white-light hover:bg-dinnerplate-white focus-visible:ring-2 focus-visible:ring-off-black focus-visible:outline-none', `size-[${ARROW_BUTTON_SIZE_PX}px]`)}
          >
            <ChevronIcon
              direction="left"
              className="transition-transform motion-reduce:transition-none"
            />
          </Button>
        </div>
      )}
      {/* Fade gradient — flips direction based on which edge we're on */}
      <div
        className={cn(
          'w-6 h-full',
          isLeft
            ? 'bg-gradient-to-r from-off-white to-transparent'
            : 'bg-gradient-to-l from-off-white to-transparent',
        )}
      />
      {/* Right variant: arrow button sits AFTER the fade */}
      {!isLeft && (
        <div className="flex h-full items-center pr-1.5 bg-off-white pointer-events-auto">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClick}
            aria-label="Scroll right"
            // tw-safelist: size-[30px]
            className={cn('rounded-full bg-dinnerplate-white-light hover:bg-dinnerplate-white focus-visible:ring-2 focus-visible:ring-off-black focus-visible:outline-none', `size-[${ARROW_BUTTON_SIZE_PX}px]`)}
          >
            <ChevronIcon
              direction="right"
              className="transition-transform motion-reduce:transition-none"
            />
          </Button>
        </div>
      )}
    </div>
  );
}

interface ScrollableTabsProps {
  tabs: readonly string[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
}

export function ScrollableTabs({
  tabs,
  activeTab,
  onTabChange,
  className,
}: ScrollableTabsProps) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Stable signal for "the tab set changed" — adding/removing/relabelling tabs
  // changes scrollWidth but not the container's own box, so a ResizeObserver
  // alone would miss it. Keyed on the joined labels so it survives callers that
  // pass a fresh array each render.
  const tabsKey = tabs.join('');

  const checkScroll = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > SCROLL_THRESHOLD_PX);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - SCROLL_THRESHOLD_PX);
  }, []);

  // Recompute arrow visibility on mount, on scroll, when the row is resized
  // (ResizeObserver), and when the tab set changes (tabsKey — the observer
  // only fires on the container's own box, not on content-width growth from
  // added/removed tabs). scrollend replaces the old post-scroll setTimeout.
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(checkScroll);
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    el.addEventListener('scrollend', checkScroll);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      el.removeEventListener('scrollend', checkScroll);
    };
  }, [checkScroll, tabsKey]);

  // Keep the active tab in view. Re-runs when the selection changes AND when
  // the tab set changes (tabsKey) — so if a reflow shifts the still-active tab
  // off-screen it is pulled back. `scroll-px-14` keeps it clear of the arrows.
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    active.scrollIntoView?.({
      behavior: prefersReduced ? 'auto' : 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, [activeTab, tabsKey]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'right' ? TAB_SCROLL_DISTANCE : -TAB_SCROLL_DISTANCE,
      behavior: 'smooth',
    });
  };

  return (
    <div className={cn('relative py-3 overflow-hidden', className)}>
      <div
        ref={tabsRef}
        role="tablist"
        onScroll={checkScroll}
        // Constant horizontal padding — the arrows/fades overlay the edges, so
        // the row never reflows when they appear/disappear (that sideways jump
        // was the source of the jerky scroll). `scroll-px-14` reserves room so
        // scrollIntoView never parks a tab under an arrow.
        className="flex items-center gap-2 overflow-x-auto px-4 scroll-px-14 [scrollbar-width:none]"
      >
        {tabs.map((tab) => (
          <Button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            variant="ghost"
            onClick={() => onTabChange(tab)}
            className={cn(
              'relative shrink-0 border rounded-md px-3 py-1.5 font-inter text-sm leading-[1.5] antialiased transition-[color,background-color,transform] hover:bg-dinnerplate-white-light hover:text-off-black motion-safe:active:scale-95 focus-visible:ring-2 focus-visible:ring-off-black focus-visible:outline-none',
              activeTab === tab
                ? 'border-transparent bg-dinnerplate-white-light font-semibold text-off-black'
                : 'border-dinnerplate-white font-normal text-grey',
            )}
          >
            <span aria-hidden="true" className="invisible font-semibold">{tab}</span>
            <span className="absolute inset-0 flex items-center justify-center">{tab}</span>
          </Button>
        ))}
      </div>
      {canScrollLeft && <ScrollArrow direction="left" onClick={() => scrollTabs('left')} />}
      {canScrollRight && <ScrollArrow direction="right" onClick={() => scrollTabs('right')} />}
    </div>
  );
}
